import { useState } from "react";
import { MessageCircle, Send } from "lucide-react";
import { waLink } from "../data/site";
import Reveal from "./Reveal";

const OPTIONS = {
  locality: ["Borivali ↔ Andheri (any)", "Andheri West", "Andheri East", "Borivali West", "Borivali East", "Kandivali", "Malad", "Goregaon"],
  bhk: ["1 BHK", "2 BHK", "3 BHK", "4 BHK+"],
  budget: ["Under ₹1.5 Cr", "₹1.5 – 2.5 Cr", "₹2.5 – 4 Cr", "₹4 Cr+"],
  purpose: ["end use", "investment"],
};

const Pill = ({ active, children, onClick, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`rounded-full border px-4 py-2 text-sm font-medium transition-all ${
      active
        ? "border-gold-deep bg-gold/15 text-[#7a5b0e]"
        : "border-[#E7E3D8] bg-white text-navy/60 hover:border-navy/30"
    }`}
  >
    {children}
  </button>
);

const Group = ({ label, children }) => (
  <div>
    <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-navy/45">{label}</p>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Matcher = () => {
  const [locality, setLocality] = useState(OPTIONS.locality[0]);
  const [bhk, setBhk] = useState(OPTIONS.bhk[1]);
  const [budget, setBudget] = useState(OPTIONS.budget[1]);
  const [purpose, setPurpose] = useState(OPTIONS.purpose[0]);

  const localityPhrase = locality.includes("↔")
    ? "between Borivali and Andheri (East/West)"
    : locality;
  const message = `Hi Shivaay Consultant! I'm looking for a ${bhk} home in ${localityPhrase}, budget ${budget}, for ${purpose}. Please share matching listings from your inventory.`;

  return (
    <section id="matcher" className="relative border-y border-[#E7E3D8] bg-white/70 py-24 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <div className="max-w-lg">
              <div className="mb-5 flex items-center gap-4">
                <span className="gold-rule" />
                <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-gold-deep">
                  Property Matcher
                </p>
              </div>
              <h2 className="font-serif text-3xl font-semibold tracking-tight text-navy sm:text-4xl lg:text-5xl">
                Tell us your need. <span className="text-gold-gradient italic">We&apos;ll do the running.</span>
              </h2>
              <p className="mt-4 text-base leading-relaxed text-navy/70">
                Pick your pocket, size and budget — we compose the WhatsApp
                message for you. A consultant replies with matched projects and
                resale options, usually within the hour.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-[#E7E3D8] bg-white p-8 shadow-[0_16px_44px_rgba(14,32,56,0.08)] sm:p-10">
              <div className="space-y-7">
                <Group label="Locality">
                  {OPTIONS.locality.map((o) => (
                    <Pill
                      key={o}
                      testid={`matcher-locality-${o.split(" ")[0].toLowerCase().replace("↔", "any")}`}
                      active={locality === o}
                      onClick={() => setLocality(o)}
                    >
                      {o}
                    </Pill>
                  ))}
                </Group>
                <Group label="Configuration">
                  {OPTIONS.bhk.map((o) => (
                    <Pill key={o} testid={`matcher-bhk-${o.replace(" ", "").toLowerCase()}`} active={bhk === o} onClick={() => setBhk(o)}>{o}</Pill>
                  ))}
                </Group>
                <Group label="Budget">
                  {OPTIONS.budget.map((o) => (
                    <Pill key={o} testid={`matcher-budget-${o.replace(/[₹\s+]/g, "").replace("–", "-")}`} active={budget === o} onClick={() => setBudget(o)}>{o}</Pill>
                  ))}
                </Group>
                <Group label="Purpose">
                  {OPTIONS.purpose.map((o) => (
                    <Pill key={o} testid={`matcher-purpose-${o.replace(" ", "-")}`} active={purpose === o} onClick={() => setPurpose(o)}>{o === "end use" ? "End use" : "Investment"}</Pill>
                  ))}
                </Group>
              </div>

              <div className="mt-8 rounded-2xl border border-[#E7E3D8] bg-ivory p-5" data-testid="matcher-message-preview">
                <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-navy/45">
                  <Send className="h-3 w-3 text-wa" />
                  Your WhatsApp message
                </p>
                <p className="mt-3 text-sm leading-relaxed text-navy/80">{message}</p>
              </div>

              <a
                href={waLink(message)}
                target="_blank"
                rel="noreferrer"
                data-testid="matcher-whatsapp-submit"
                className="mt-6 flex items-center justify-center gap-3 rounded-full bg-wa px-8 py-4 text-base font-bold text-white shadow-[0_10px_28px_rgba(31,175,94,0.35)] transition-all hover:bg-[#189a51]"
              >
                <MessageCircle className="h-5 w-5" />
                Send on WhatsApp
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Matcher;
